import Device from '@/util/Device'
import Sentry from './Sentry'
import importExternal from '@/util/importExternal'
import { gatherAnalyticsData } from '@/util/helpers'

/**
 * ANALYTICS & ERROR TRACKING DOCUMENTATION
 * ========================================
 * 
 * This service handles all analytics tracking through Google Tag Manager (GTM).
 * 
 * ERROR TRACKING:
 * All error tracking goes through GTM via the trackError() method (line 148-150).
 * Error events are sent to GTM's dataLayer and then configured/processed through
 * the GTM interface.
 * 
 * TO ADD NEW ERROR TRACKING PROPERTIES:
 * 1. Go to https://tagmanager.google.com
 * 2. Access the GTM container for this application
 * 3. Configure new variables, triggers, or tags as needed
 * 4. The error data sent from trackError() will be available in GTM's dataLayer
 * 
 * GTM INTEGRATION:
 * - All events (including errors) are pushed to global.dataLayer
 * - GTM processes these events based on its configuration
 * - Properties can be added/modified in GTM without code changes
 * 
 * AVAILABLE ERROR DATA:
 * - Error events include comprehensive analytics data (gathered via gatherAnalyticsData)
 * - Device information (display_mode, platform details)
 * - Custom meta data passed to trackError()
 */

// Initialize GTM if ID is available (browser only; skip during SSR)
if (typeof window !== 'undefined' && import.meta.env.VITE_GTM_ID) {
  initGTM(import.meta.env.VITE_GTM_ID)
}

/** GTM init */
async function initGTM(gtmId) {
  global.dataLayer = global.dataLayer || []
  const options = {
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  }

  global.dataLayer.push(options)
  importExternal(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`)
}

class AnalyticsService {
  /**
   * Main tracking method - sends all events (including errors) to GTM
   * 
   * ALL ANALYTICS & ERROR TRACKING flows through this method to GTM's dataLayer.
   * To modify tracking properties or add new ones, use https://tagmanager.google.com
   * 
   * @param {string} event - Event name (e.g., 'ErrorPageView', 'WagerBuy', etc.)
   * @param {Object} data - Event-specific data
   * @param {Object} meta - Additional metadata
   */
  async track(event, data, meta) {
    try {
      // Gather comprehensive analytics data
      const analyticsData = await gatherAnalyticsData()
      
      if (global.dataLayer) {
        global.dataLayer.push({
          event,
          data,
          extra: {
            display_mode: Device.displayMode,
            // Include all comprehensive analytics data
            ...analyticsData,
            // Include any additional meta data
            ...meta,
          },
        })
      }
    } catch (error) {
      console.warn('Failed to gather analytics data for event:', event, error)
      // Fallback to original behavior if analytics gathering fails
      if (global.dataLayer) {
        global.dataLayer.push({
          event,
          data,
          extra: {
            display_mode: Device.displayMode, 
            ...meta,
          },
        })
      }
    }
  }

  async trackWagerBuy(data, meta) {
    await this.track('WagerBuy', data, meta)
  }

  async trackWagerSell(data, meta) {
    await this.track('WagerSell', data, meta)
  }

  async trackWagerSellAll(data, meta) {
    await this.track('WagerSellAll', data, meta)
  }

  async trackMe(data, meta) {
    await this.track('Me', data, meta)
  }

  async trackRating(data, meta) {
    await this.track('Rating', data, meta)
  }

  trackWagerBuyRecorrence(data) {
    this.track('WagerBuyRecorrence', { wagers_count: data.wagers_count })
  }

  async trackRegistration(data) {
    await this.track('Registration', { email: data.email })
  }

  /**
   * Track error events through GTM
   * 
   * ERROR TRACKING FLOW:
   * 1. This method sends error data to GTM's dataLayer via track()
   * 2. GTM processes the 'ErrorPageView' event based on its configuration
   * 3. To modify error tracking properties, go to https://tagmanager.google.com
   * 
   * @param {Object} data - Error-specific data (error message, stack trace, etc.)
   * @param {Object} meta - Additional metadata for the error
   */
  async trackError(data, meta) {
    await this.track('ErrorPageView', data, meta)
  }

  trackLogs(data) {
    // Sentry.log(
    //   new Error('Loging version number', {
    //     currentAppVersion: data,
    //   })
    // )
  }

  addParamsToGTM(params) {
    global.dataLayer = global.dataLayer || []
    global.dataLayer.push(params)
  }
}

const instance = new AnalyticsService()
export default instance 